'use client'

import Script from 'next/script'
import { useCallback, useEffect, useRef, useState } from 'react'

import styles from './VenueMap.module.scss'
import type { VenueMapProps } from './VenueMap.types'

/** Clave pública del SDK de Woosmap — variable de build, inlinada en el
 * bundle de cliente (research.md §12/§8; ver `.env.example`). */
const WOOSMAP_KEY = 'woos-bd23f494-4d67-358c-b24d-62a6f8456b37'

/** Icono del marcador — mismo PNG que el proyecto antiguo (pin rojo con
 * ticket), asset cuadrado de 512×512px; se muestra reducido a 40×40. */
const MARKER_ICON = {
  url: '/entradas/icons/icon_location_maps.png',
  size: { width: 40, height: 40 },
  scaledSize: { width: 40, height: 40 },
}

/** Margen con el que se empieza a cargar el SDK antes de que el mapa entre en
 * pantalla: lo justo para que esté pintado al llegar a él con scroll normal. */
const LOAD_ROOT_MARGIN = '600px 0px'

/** Pie de atribución que pinta el SDK: un `role="list"` con los enlaces
 * directamente dentro, sin `listitem` (WCAG 1.3.1). */
const ATTRIBUTION_LIST_SELECTOR = '.mapboxgl-ctrl-attrib-inner[role="list"]'

/**
 * Mapa interactivo de la ubicación de un recinto, pintado con el SDK de
 * mapas de Woosmap (`https://sdk.woosmap.com/map/map.js`, script/clave/
 * inicialización dados explícitamente por el encargo — research.md §1/§8).
 *
 * Única pieza de cliente de todo el módulo `Recinto` (Constitución,
 * Principio I: `'use client'` empujado a la hoja) — recibe solo props
 * serializables (`center`).
 *
 * Se inicializa siempre con `center` (la posición real del recinto, o el
 * centro por defecto en Madrid cuando no hay posición — FR-010): el mapa
 * nunca queda vacío, esté o no disponible la posición real del recinto
 * (SC-005). La diferencia entre ambos casos (mostrar o no el botón "Cómo
 * llegar") la decide `Recinto.tsx`, no este componente.
 */
export default function VenueMap({ center }: VenueMapProps) {
  const mapElementRef = useRef<HTMLDivElement>(null)

  const mapCreatedRef = useRef(false)

  // El SDK de Woosmap es la tarea más larga de la carga de la home (~1,5 s de
  // hilo principal con la CPU de un móvil medio, Lighthouse) y el mapa suele
  // quedar muy por debajo del primer pantallazo: no se descarga ni se crea
  // hasta que el mapa se acerca a la pantalla (`LOAD_ROOT_MARGIN`).
  const [nearViewport, setNearViewport] = useState(false)
  useEffect(() => {
    const container = mapElementRef.current
    if (!container || nearViewport) return
    if (typeof IntersectionObserver === 'undefined') {
      // Sin IntersectionObserver (navegadores muy antiguos, jsdom) se carga
      // directamente, como antes.
      // eslint-disable-next-line react-hooks/set-state-in-effect -- capacidad del navegador, solo conocida tras montar
      setNearViewport(true)
      return
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          observer.disconnect()
          setNearViewport(true)
        }
      },
      { rootMargin: LOAD_ROOT_MARGIN },
    )
    observer.observe(container)
    return () => observer.disconnect()
  }, [nearViewport])

  const createMap = useCallback(() => {
    const container = mapElementRef.current
    const woosmap = window.woosmap
    if (!nearViewport || !container || !woosmap || mapCreatedRef.current) return
    mapCreatedRef.current = true

    // `gestureHandling: 'cooperative'` — evita capturar el scroll de la
    // página con un dedo sobre el mapa en móvil (Ctrl/⌘+scroll o dos dedos).
    const map = new woosmap.map.Map(container, {
      center,
      zoom: 15,
      gestureHandling: 'cooperative',
    })
    new woosmap.map.Marker({ position: center, map, icon: MARKER_ICON })
  }, [center, nearViewport])

  useEffect(() => {
    // Copia local: en el cleanup `mapElementRef.current` ya puede ser `null`
    // (React desengancha el ref al desmontar antes de correr el cleanup).
    const container = mapElementRef.current

    // Aviso de desarrollo, no un fallo silencioso: sin clave, Woosmap
    // responde 403 a la carga del script — más difícil de diagnosticar que
    // un aviso explícito aquí (ver nota de diagnóstico 403 más abajo).
    if (!WOOSMAP_KEY && process.env.NODE_ENV !== 'production') {
      console.error(
        'VenueMap: falta NEXT_PUBLIC_WOOSMAP_KEY — el SDK de Woosmap responderá 403 sin una clave.',
      )
    }

    // Si el SDK ya estaba cargado (navegación cliente, otro VenueMap previo),
    // `next/script` no vuelve a ejecutarlo y `onReady` puede no dispararse a
    // tiempo: se crea el mapa aquí. En la primera carga lo crea `onReady`.
    createMap()

    // El SDK pinta la atribución de forma asíncrona (y la repinta al cambiar
    // de tamaño): se le quita el `role="list"` mal formado cada vez que
    // aparece. Solo observa el propio contenedor del mapa.
    const attributionObserver = new MutationObserver(() => {
      container
        ?.querySelectorAll(ATTRIBUTION_LIST_SELECTOR)
        .forEach((el) => el.removeAttribute('role'))
    })
    if (container) attributionObserver.observe(container, { childList: true, subtree: true })

    // El SDK de Woosmap pide de forma asíncrona (fuera de nuestro control:
    // no hay ninguna promesa propia que envolver en try/catch) el estilo del
    // mapa tras `initMap`. Cuando la clave no está autorizada para el
    // dominio/referer actual, esa petición interna rechaza con un objeto
    // plano `{ status: 403, ... }` (no un `Error`) y queda sin capturar —
    // `unhandledRejection: [object Object]` en consola, sin traza útil (bug
    // confirmado en dev vía la página CMS "pruebas"). Se marca como
    // manejado y se deja un aviso legible en su lugar.
    function handleWoosmapRejection(event: PromiseRejectionEvent) {
      const reason = event.reason
      const isWoosmapAuthError =
        typeof reason === 'object' && reason !== null && 'status' in reason && reason.status === 403
      if (!isWoosmapAuthError) return
      event.preventDefault()
      console.error(
        'VenueMap: el SDK de Woosmap rechazó una petición interna con 403 — la clave no está ' +
          'autorizada para este dominio/referer (revisa las restricciones de dominio en el panel de Woosmap).',
      )
    }
    window.addEventListener('unhandledrejection', handleWoosmapRejection)

    return () => {
      attributionObserver.disconnect()
      window.removeEventListener('unhandledrejection', handleWoosmapRejection)
      // El SDK no expone destroy: se vacía el contenedor para poder recrear.
      container?.replaceChildren()
      mapCreatedRef.current = false
    }
  }, [center, createMap])

  return (
    <div className={styles.mapWrap}>
      {/* `region`, no `img`: el mapa tiene controles enfocables dentro (zoom,
          atribución) y un rol de imagen los ocultaría a los lectores de
          pantalla aunque el teclado siga llegando a ellos (WCAG 4.1.2). */}
      <div
        ref={mapElementRef}
        className={styles.map}
        role="region"
        aria-label="Mapa de ubicación del recinto"
      />
      {nearViewport && (
        <Script
          src={`https://sdk.woosmap.com/map/map.js?language=es&key=${WOOSMAP_KEY}`}
          strategy="afterInteractive"
          // `onReady` corre tras la primera carga Y en cada montaje posterior con
          // el script ya cargado (el `callback=` de la URL solo se disparaba una vez).
          onReady={createMap}
          // Diagnóstico de la incidencia de 403 reportada: un fallo de carga
          // del script (clave no autorizada para este dominio, cuota agotada,
          // etc.) hoy no deja ningún rastro visible más allá del propio 403 en
          // la pestaña Red — este `onError` lo deja también en consola con
          // contexto, para no tener que adivinarlo cada vez.
          onError={() => {
            console.error(
              'VenueMap: no se pudo cargar el script del SDK de Woosmap (revisa la pestaña Red — ' +
                'un 403 aquí normalmente significa que el dominio actual no está autorizado para ' +
                'esta clave en el panel de Woosmap, no un fallo de este componente).',
            )
          }}
        />
      )}
    </div>
  )
}
